



































































































































































































































import Vue from 'vue';
import dayjs from 'dayjs';
import api from '../services/api';
import eventBus from '@/services/eventBus';
import DateInput from '@/components/DateInput.vue';
import LookupComboBox from '@/modules/transactions/components/LookupComboBox.vue';
import ValidationError from '@/modules/downPayment/components/ValidationError.vue';
import TransactionApi from '@/modules/transactions/services/TransactionApi';

interface Data {
    accounts: any[];
    dimensions: any[];
    bookTypes: any[];
    management: string; // '+' = Management, '-' = Vegyes (K2D w_dim_mod "Típus" rádió)
    fromAccno: string;
    fromAccnoDisplay: string;
    toAccno: string;
    toAccnoDisplay: string;
    fromDimcode: string;
    fromDimcodeDisplay: string;
    toDimcode: string;
    toDimcodeDisplay: string;
    users: any[];
    stations: any[];
    tools: any[];
    fromUserId: number;
    fromUserDisplay: string;
    toUserId: number;
    toUserDisplay: string;
    fromStationId: number;
    fromStationDisplay: string;
    toStationId: number;
    toStationDisplay: string;
    fromToolId: number;
    fromToolDisplay: string;
    toToolId: number;
    toToolDisplay: string;
    amount: number;
    fromDate: string;
    toDate: string;
    accDate: string;
    loadedYear: number; // melyik évre van betöltve az accno+dimcode lista (accDate évéhez kötve)
    accEvent: string;
    remark: string;
    busy: boolean;
}
interface Methods {
    loadAccounts(): Promise<void>;
    loadDimensions(): Promise<void>;
    loadBookTypes(): Promise<void>;
    loadUsers(): Promise<void>;
    loadStations(): Promise<void>;
    loadTools(): Promise<void>;
    onFromAccnoChange(selected: any): void;
    onToAccnoChange(selected: any): void;
    onFromDimcodeChange(selected: any): void;
    onToDimcodeChange(selected: any): void;
    clearUserPair(): void;
    clearStationPair(): void;
    clearToolPair(): void;
    onFromUserChange(selected: any): void;
    onToUserChange(selected: any): void;
    onFromStationChange(selected: any): void;
    onToStationChange(selected: any): void;
    onFromToolChange(selected: any): void;
    onToToolChange(selected: any): void;
    book(): Promise<void>;
}
interface Computed {
    bookTypeId: number;
    bookTypeDisplay: string;
    error: string;
    canBook: boolean;
}

export default Vue.extend<Data, Methods, Computed, {}>({
    components: { DateInput, LookupComboBox, ValidationError },
    data() {
        return {
            accounts: [],
            dimensions: [],
            bookTypes: [],
            management: '+', // K2D default: Management (-> book_type 18)
            fromAccno: '',
            fromAccnoDisplay: '',
            toAccno: '',
            toAccnoDisplay: '',
            fromDimcode: '',
            fromDimcodeDisplay: '',
            toDimcode: '',
            toDimcodeDisplay: '',
            users: [],
            stations: [],
            tools: [],
            // 1 = "nincs" sentinel (ixboktrans_d kötelező értéke); a megjelenítés üres, amíg nem választanak.
            fromUserId: 1,
            fromUserDisplay: '',
            toUserId: 1,
            toUserDisplay: '',
            fromStationId: 1,
            fromStationDisplay: '',
            toStationId: 1,
            toStationDisplay: '',
            fromToolId: 1,
            fromToolDisplay: '',
            toToolId: 1,
            toToolDisplay: '',
            amount: 0,
            fromDate: '',
            toDate: '',
            accDate: '',
            loadedYear: 0,
            accEvent: '',
            remark: '',
            busy: false,
        };
    },
    async mounted() {
        await this.loadBookTypes();
        // Erőforrás-dimenzió törzsek (a GL tranzakció-szerkesztő "egyéb dimenzió" füle is ezeket használja).
        await Promise.all([this.loadUsers(), this.loadStations(), this.loadTools()]);
        // A könyvelés dátuma alapból a tárgyhó utolsó napja (K2D: of_lastdayofmonth);
        // a beállítás a watcheren át tölti az intervallumot + a (dátum évéhez tartozó) főkönyvi szám/dimenzió listát.
        this.accDate = dayjs().endOf('month').format('YYYY-MM-DD');
    },
    watch: {
        // A könyvelés dátuma vezérel mindent: az intervallumot a tárgyhóra, az évváltáskor pedig
        // a (más évhez tartozó) forrás/cél választást ürítjük és újratöltjük az adott év listáit.
        accDate(val) {
            if (!val) {
                return;
            }
            const d = dayjs(val);
            // Csak a hónap utolsó napja engedélyezett (K2D megszorítás); egyébként jelzünk és magunktól arra állítjuk.
            // FONTOS: a korrekciót $nextTick-ben tesszük – különben a Vue ugyanabban a tickben "visszavonja" a
            // változást (a DateInput value propja nettó nem változik), és a kijelzés a rosszul választott napon ragad
            // ugyanazon a hónapon belül (más hónapra váltáskor működne, mert ott valódi a prop-változás).
            if (d.date() !== d.daysInMonth()) {
                eventBus.$emit(
                    'error',
                    'A könyvelés dátuma csak a hónap utolsó napja lehet – a hónap utolsó napjára állítottam.',
                );
                const lastDay = d.endOf('month').format('YYYY-MM-DD');
                this.$nextTick(() => {
                    this.accDate = lastDay;
                });
                return;
            }
            this.fromDate = d.startOf('month').format('YYYY-MM-DD');
            this.toDate = d.endOf('month').format('YYYY-MM-DD');

            // Évváltáskor (a betöltött évhez képest) a korábbi accno/dimcode már nem érvényes – ürítjük és újratöltünk.
            const year = d.year();
            if (year !== this.loadedYear) {
                this.loadedYear = year;
                this.fromAccno = '';
                this.fromAccnoDisplay = '';
                this.toAccno = '';
                this.toAccnoDisplay = '';
                this.fromDimcode = '';
                this.fromDimcodeDisplay = '';
                this.toDimcode = '';
                this.toDimcodeDisplay = '';
                this.loadAccounts();
                this.loadDimensions();
            }
        },
    },
    methods: {
        // A könyvelés dátumának évéhez tartozó aktív főkönyvi számok (mmbokaccno) – a forrás/cél választóhoz.
        async loadAccounts() {
            const year = this.accDate ? dayjs(this.accDate).year() : 0;
            if (!year) {
                this.accounts = [];
                return;
            }
            try {
                this.accounts = (await api.GetInvoiceMirrorsByYear(year)) || [];
            } catch (e) {
                eventBus.$emit('error', 'A főkönyvi számok betöltése nem sikerült!');
                this.accounts = [];
            }
        },
        // A költséghely-kódok (mmbokdimcode) a könyvelés dátumának évére – a K2D w_dim_mod-dal azonos szűrővel
        // (type=2 + év, status/gyűjtő szűrés NÉLKÜL). NEM a tranzakció-szerkesztő szűkebb getDimensions-e!
        async loadDimensions() {
            const year = this.accDate ? dayjs(this.accDate).year() : 0;
            if (!year) {
                this.dimensions = [];
                return;
            }
            try {
                this.dimensions = (await api.GetCostCenterDimcodes(year)) || [];
            } catch (e) {
                this.dimensions = [];
            }
        },
        // Naplótípusok (csbokbooktype) a Napló típus legördülőhöz.
        async loadBookTypes() {
            try {
                this.bookTypes = await TransactionApi.getBookTypes();
            } catch (e) {
                this.bookTypes = [];
            }
        },
        // Felhasználó/erőforrás törzs (msgenuser) – a Felhasználóról/ra választóhoz.
        async loadUsers() {
            try {
                this.users = (await TransactionApi.getUsers()) || [];
            } catch (e) {
                this.users = [];
            }
        },
        // Munkaállomás törzs (csserstation) – a Munkaállomásról/ra választóhoz.
        async loadStations() {
            try {
                this.stations = (await TransactionApi.getStations()) || [];
            } catch (e) {
                this.stations = [];
            }
        },
        // Szerszám törzs (mxstrparts, tool-ra szűrve a backend GetTools-ban) – a Szerszámról/ra választóhoz.
        async loadTools() {
            try {
                this.tools = (await TransactionApi.getTools()) || [];
            } catch (e) {
                this.tools = [];
            }
        },
        onFromAccnoChange(selected) {
            this.fromAccno = selected && selected.accno ? selected.accno : '';
            this.fromAccnoDisplay = this.fromAccno;
            // K2D w_dim_mod: a forrás fkv. szám kiválasztása a CÉLT is ugyanarra állítja (utána a cél külön módosítható).
            this.toAccno = this.fromAccno;
            this.toAccnoDisplay = this.fromAccno;
        },
        onToAccnoChange(selected) {
            this.toAccno = selected && selected.accno ? selected.accno : '';
            this.toAccnoDisplay = this.toAccno;
        },
        onFromDimcodeChange(selected) {
            this.fromDimcode = selected && selected.dimcode ? selected.dimcode : '';
            this.fromDimcodeDisplay = this.fromDimcode;
        },
        onToDimcodeChange(selected) {
            this.toDimcode = selected && selected.dimcode ? selected.dimcode : '';
            this.toDimcodeDisplay = this.toDimcode;
        },
        // Erőforrás-dimenzió választók: üres választás -> 1 ("nincs"), különben a törzs id-ja.
        // K2D w_dim_mod dw_1::itemchanged: a Felhasználó / Munkaállomás / Szerszám KIZÁRJÁK egymást —
        // ha az egyik fajtából valódi értéket választunk, a másik két fajta ról/ra párja "nincs"-re (1) áll.
        clearUserPair() {
            this.fromUserId = 1;
            this.toUserId = 1;
            this.fromUserDisplay = '';
            this.toUserDisplay = '';
        },
        clearStationPair() {
            this.fromStationId = 1;
            this.toStationId = 1;
            this.fromStationDisplay = '';
            this.toStationDisplay = '';
        },
        clearToolPair() {
            this.fromToolId = 1;
            this.toToolId = 1;
            this.fromToolDisplay = '';
            this.toToolDisplay = '';
        },
        onFromUserChange(selected) {
            this.fromUserId = selected && selected.id ? selected.id : 1;
            this.fromUserDisplay = selected && selected.name ? selected.name : '';
            if (this.fromUserId > 1) {
                this.clearStationPair();
                this.clearToolPair();
            }
        },
        onToUserChange(selected) {
            this.toUserId = selected && selected.id ? selected.id : 1;
            this.toUserDisplay = selected && selected.name ? selected.name : '';
            if (this.toUserId > 1) {
                this.clearStationPair();
                this.clearToolPair();
            }
        },
        onFromStationChange(selected) {
            this.fromStationId = selected && selected.id ? selected.id : 1;
            this.fromStationDisplay = selected && selected.name ? selected.name : '';
            if (this.fromStationId > 1) {
                this.clearUserPair();
                this.clearToolPair();
            }
        },
        onToStationChange(selected) {
            this.toStationId = selected && selected.id ? selected.id : 1;
            this.toStationDisplay = selected && selected.name ? selected.name : '';
            if (this.toStationId > 1) {
                this.clearUserPair();
                this.clearToolPair();
            }
        },
        onFromToolChange(selected) {
            this.fromToolId = selected && selected.id ? selected.id : 1;
            this.fromToolDisplay = selected && selected.name ? selected.name : '';
            if (this.fromToolId > 1) {
                this.clearUserPair();
                this.clearStationPair();
            }
        },
        onToToolChange(selected) {
            this.toToolId = selected && selected.id ? selected.id : 1;
            this.toToolDisplay = selected && selected.name ? selected.name : '';
            if (this.toToolId > 1) {
                this.clearUserPair();
                this.clearStationPair();
            }
        },
        async book() {
            if (!this.canBook) {
                return;
            }
            this.busy = true;
            try {
                const res = await api.PostDimensionTransfer({
                    fromAccno: this.fromAccno,
                    toAccno: this.toAccno,
                    amount: this.amount,
                    fromDate: this.fromDate,
                    toDate: this.toDate,
                    accDate: this.accDate,
                    fromDimcode: this.fromDimcode,
                    toDimcode: this.toDimcode,
                    bookType: this.bookTypeId,
                    accEvent: this.accEvent,
                    remark: this.remark,
                    fromUserId: this.fromUserId,
                    toUserId: this.toUserId,
                    fromStationId: this.fromStationId,
                    toStationId: this.toStationId,
                    fromToolId: this.fromToolId,
                    toToolId: this.toToolId,
                });
                if (res.success) {
                    eventBus.$emit('success', res.message);
                    // Új átvezetéshez ürítjük az összeget (a többi mező marad a kényelemért).
                    this.amount = 0;
                } else {
                    eventBus.$emit('error', res.message);
                }
            } catch (e) {
                eventBus.$emit('error', 'A dimenzió átkönyvelés nem sikerült!');
            } finally {
                this.busy = false;
            }
        },
    },
    computed: {
        // K2D w_dim_mod: a "Típus" választja a naplótípust, a K2D HARDCODE-olja (dw_1::itemchanged: if_os(data='+',18,11)):
        // Management -> 18, Vegyes -> 11. FONTOS: NEM flag-alapon (is_manage_cont '+' több "(man)" típusra is igaz:
        // 16/17/18), mert akkor a rossz book_type-ot (16) választanánk, ami az SP-ben a T/K előjelet is felcseréli.
        bookTypeId() {
            return this.management === '+' ? 18 : 11;
        },
        // A naplótípus csak kijelző (K2D dddw allowedit=no): a kiválasztott book_type "kód - név" formában.
        bookTypeDisplay() {
            const bt = this.bookTypes.find((b: any) => b.id === this.bookTypeId);
            if (!bt) {
                return '';
            }
            return bt.code ? `${bt.code} - ${bt.name}` : bt.name;
        },
        // A könyvelés előfeltételei – az első hiányzó kötelező mező üzenete jelenik meg a piros felkiáltójelnél.
        error() {
            if (!this.accDate) {
                return 'A könyvelés dátuma kitöltése kötelező!';
            }
            if (!this.fromAccno) {
                return 'A forrás főkönyvi szám kiválasztása kötelező!';
            }
            if (!this.fromDimcode) {
                return 'A forrás dimenzió-kód (költséghely) kiválasztása kötelező!';
            }
            if (!this.toDimcode) {
                return 'A cél dimenzió-kód (költséghely) kiválasztása kötelező!';
            }
            if (!this.amount || this.amount <= 0) {
                return 'Az átvezetendő összegnek nullánál nagyobbnak kell lennie!';
            }
            if (!this.accEvent) {
                return 'A gazdasági esemény kitöltése kötelező!';
            }
            // K2D w_dim_mod dw_1::itemchanged: ugyanaz az erőforrás nem választható egyszerre
            // "ról" és "ra" oldalra (1 = "nincs" sentinel, arra nem vonatkozik a megszorítás).
            if (this.fromUserId > 1 && this.fromUserId === this.toUserId) {
                return 'A felhasználó nem lehet ugyanaz a "ról" és "ra" mezőben!';
            }
            if (this.fromStationId > 1 && this.fromStationId === this.toStationId) {
                return 'A munkaállomás nem lehet ugyanaz a "ról" és "ra" mezőben!';
            }
            if (this.fromToolId > 1 && this.fromToolId === this.toToolId) {
                return 'A szerszám nem lehet ugyanaz a "ról" és "ra" mezőben!';
            }
            return '';
        },
        canBook() {
            return this.error === '';
        },
    },
});
